import React, { useState, useRef, useEffect } from 'react';
import styles from './ChatAssistant.module.css';

const PREDEFINED_QUESTIONS = [
    'What can you do?',
    'How can AstroStar help my business?',
    'Connect me with AstroStar representative.'
];

interface Message {
    from: 'user' | 'bot';
    text: string;
    timestamp?: string; // optional for backward compatibility
}

interface ChatPopupProps {
    open: boolean;
    onClose: () => void;
    onSend: (msg: string) => void;
    messages: Message[];
    onPredefined: (msg: string) => void;
    animating?: boolean;
    closing?: boolean;
    loading?: boolean;
    onContactInfo?: (info: { email: string; phone?: string }) => void;
}

function getCurrentTimeHHMM() {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

const INTRO_MSG = "Hi, I'm AstroStar Assistant powered by AI. I can answer your questions and connect to Sales Expert.";

const LoadingEllipsis: React.FC = () => (
  <span style={{
    display: 'inline-block',
    minWidth: 32,
    textAlign: 'left',
    letterSpacing: 2,
    fontSize: '3.2em'
  }}>
    <span className="dot-anim">.</span>
    <span className="dot-anim" style={{ animationDelay: '0.2s' }}>.</span>
    <span className="dot-anim" style={{ animationDelay: '0.4s' }}>.</span>
    <style>
      {`
        .dot-anim {
          opacity: 0.3;
          animation: blink 1s infinite;
        }
        @keyframes blink {
          0% { opacity: 0.3; }
          20% { opacity: 1; }
          100% { opacity: 0.3; }
        }
      `}
    </style>
  </span>
);

const ChatPopup: React.FC<ChatPopupProps> = ({ open, onClose, onSend, messages, onPredefined, animating, closing, loading, onContactInfo }) => {
    const [input, setInput] = useState('');
    const [openedAt, setOpenedAt] = useState<string | null>(null);
    const [collectingContact, setCollectingContact] = useState(false);
    const [contactStep, setContactStep] = useState<'email' | 'phone' | 'done'>('email');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [contactError, setContactError] = useState('');
    const [contactConfirmed, setContactConfirmed] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Set openedAt when chat is opened
    useEffect(() => {
        if (open && !openedAt) {
            setOpenedAt(getCurrentTimeHHMM());
        }
        if (!open && openedAt) {
            setOpenedAt(null);
        }
    }, [open, openedAt]);

    // Scroll to bottom on new message
    useEffect(() => {
        if (open && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, open]);

    // Animation classes
    let popupClass = styles.chatPopup;
    if (open && animating) popupClass += ' ' + styles.chatPopupUnroll + ' ' + styles.chatPopupOpen;
    else if (open) popupClass += ' ' + styles.chatPopupOpen;
    else if (closing) popupClass += ' ' + styles.chatPopupClose;
    else popupClass += '';

    // Watch for trigger phrase in messages to start contact collection
    useEffect(() => {
        if (!collectingContact && messages.length > 0) {
            const last = messages[messages.length - 1];
            if (
                last.from === 'user' &&
                /connect.*(representative|sales|human|expert|someone)/i.test(last.text)
            ) {
                setCollectingContact(true);
                setContactStep('email');
                setContactEmail('');
                setContactPhone('');
                setContactError('');
                setContactConfirmed(false);
            }
        }
    }, [messages, collectingContact]);

    // Notify parent when contact info is collected
    useEffect(() => {
        if (collectingContact && contactConfirmed && contactEmail) {
            if (typeof onContactInfo === 'function') {
                onContactInfo({ email: contactEmail, phone: contactPhone });
            }
        }
    }, [collectingContact, contactConfirmed, contactEmail, contactPhone, onContactInfo]);

    // Helper to get timestamp for a message
    const getMsgTimestamp = (msg: Message, idx: number): string => {
        if (msg.timestamp) return msg.timestamp;
        // fallback: if not present, use current time (not ideal, but for legacy)
        return getCurrentTimeHHMM();
    };

    // Helper to render timestamp label
    const renderTimestamp = (msg: Message, idx: number) => {
        const time = getMsgTimestamp(msg, idx);
        if (msg.from === 'user') {
            return <div style={{ fontSize: '0.85em', color: '#888', marginTop: 2, marginLeft: 2 }}>Sent {time};</div>;
        } else {
            return <div style={{ fontSize: '0.85em', color: '#888', marginTop: 2, marginLeft: 2 }}>AstroStar {time};</div>;
        }
    };

    // Block: AstroStar joined, intro, predef, timestamp
    const renderIntroBlock = () => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6, padding: '12px 16px 0 16px', background: '#f7f7f7', borderBottom: '1px solid #eee' }}>
            <div style={{ textAlign: 'center', fontSize: '0.9em', color: '#888', marginBottom: 4 }}>AstroStar joined</div>
             <div className={styles.msg + ' ' +styles.msgBot}>{INTRO_MSG}</div>
          {/* <div style={{ fontSize: '1em', color: '#222', marginBottom: 8, textAlign: 'left', fontWeight: 500 }}>{INTRO_MSG}</div> */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {PREDEFINED_QUESTIONS.map(q => (
                    <button key={q} className={styles.predefBtn} onClick={() => onPredefined(q)}>{q}</button>
                ))}
            </div>
            <div style={{ fontSize: '0.85em', color: '#888', marginTop: 8, textAlign: 'left' }}>{openedAt ? `AstroStar - ${openedAt}` : ''}</div>
        </div>
    );

    return (
        <div className={popupClass} style={{ pointerEvents: open ? 'auto' : 'none' }}>
            <div className={styles.chatHeader}>
                <span>Ask AstroStar</span>
                <button className={styles.closeBtn} onClick={onClose} title="Close">×</button>
            </div>
            {renderIntroBlock()}
            <div className={styles.convArea}>
                {messages
                    .filter((m) => m.text !== "Hi, I'm AstroStar Assistant powered by AI. I can answer your questions and connect to Sales Expert.'")
                    .map((m, i) => (
                        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.from === 'user' ? 'flex-end' : 'flex-start' }}>
                            <div
                                className={
                                    styles.msg + ' ' + (m.from === 'user' ? styles.msgUser : styles.msgBot)
                                }
                            >
                                {m.text}
                            </div>
                            {renderTimestamp(m, i)}
                        </div>
                    ))}
                {collectingContact && !contactConfirmed && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginTop: 8 }}>
                        <div className={styles.msg + ' ' + styles.msgBot}>
                            {contactStep === 'email' && (
                                <>
                                    Please enter your email so our sales expert can contact you.<br />
                                    <form onSubmit={e => {
                                        e.preventDefault();
                                        if (!contactEmail.trim() || !/^\S+@\S+\.\S+$/.test(contactEmail)) {
                                            setContactError('Please enter a valid email address.');
                                            return;
                                        }
                                        setContactStep('phone');
                                        setContactError('');
                                    }} style={{ marginTop: 8 }}>
                                        <input
                                            type="email"
                                            value={contactEmail}
                                            onChange={e => setContactEmail(e.target.value)}
                                            placeholder="Email address"
                                            style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', width: 240, fontSize: 15 }}
                                            autoFocus
                                        />
                                        <button type="submit" style={{ marginLeft: 8, padding: '8px 16px', borderRadius: 6, border: 'none', background: '#00b1a2', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Next</button>
                                    </form>
                                    {contactError && <div style={{ color: 'red', fontSize: 13, marginTop: 4 }}>{contactError}</div>}
                                </>
                            )}
                            {contactStep === 'phone' && (
                                <>
                                    (Optional) Enter your phone for a faster response, or just click Submit.<br />
                                    <form onSubmit={e => {
                                        e.preventDefault();
                                        // Accept empty or valid phone
                                        if (contactPhone && !/^\+?\d{6,15}$/.test(contactPhone)) {
                                            setContactError('Please enter a valid phone number or leave blank.');
                                            return;
                                        }
                                        setContactStep('done');
                                        setContactError('');
                                        setContactConfirmed(true);
                                    }} style={{ marginTop: 8 }}>
                                        <input
                                            type="tel"
                                            value={contactPhone}
                                            onChange={e => setContactPhone(e.target.value)}
                                            placeholder="Phone number (optional)"
                                            style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', width: 180, fontSize: 15 }}
                                        />
                                        <button type="submit" style={{ marginLeft: 8, padding: '8px 16px', borderRadius: 6, border: 'none', background: '#00b1a2', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Submit</button>
                                    </form>
                                    {contactError && <div style={{ color: 'red', fontSize: 13, marginTop: 4 }}>{contactError}</div>}
                                </>
                            )}
                        </div>
                    </div>
                )}
                {collectingContact && contactConfirmed && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginTop: 8 }}>
                        <div className={styles.msg + ' ' + styles.msgBot}>
                            Thank you! Your contact info has been received. Our sales expert will reach out to you soon at <b>{contactEmail}</b>{contactPhone ? ` or ${contactPhone}` : ''}.
                        </div>
                    </div>
                )}
                {loading && !collectingContact && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div className={styles.msg + ' ' + styles.msgBot}>
                      <LoadingEllipsis />
                    </div>
                    <div style={{ fontSize: '0.85em', color: '#888', marginTop: 2, marginLeft: 2 }}>AstroStar ...</div>
                  </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            {/* Only show input if not collecting contact */}
            {!collectingContact && (
                <form className={styles.inputRow} onSubmit={e => { e.preventDefault(); if (input.trim()) { onSend(input); setInput(''); } }}>
                    <textarea
                        className={styles.inputBox}
                        value={input}
                        onChange={e => {
                            setInput(e.target.value);
                            // Auto-expand textarea
                            const ta = e.target as HTMLTextAreaElement;
                            ta.style.height = 'auto';
                            ta.style.height = ta.scrollHeight + 'px';
                        }}
                        placeholder="Ask me anything..."
                        rows={1}
                        style={{ resize: 'none', overflow: 'hidden', minHeight: 36, maxHeight: 120 }}
                    />
                    <button type="submit" className={styles.sendBtn} aria-label="Send">
                        {/* SVG send icon */}
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00b1a2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                    </button>
                </form>
            )}
        </div>
    );
};

export default React.memo(ChatPopup);
